import { classes } from "polytype";
import assertObjectFound from "../../base/assertObjectFound.js";
import {
  AggregateMetaData,
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import { config } from "../../config/config.js";
import { Project } from "../../project/index.js";
import { provideReact, type AsyncResourceVariant } from "../../react/index.js";
import type {
  RegistryCreateRequestData,
  RegistryData,
  RegistryListItemData,
  RegistryListQueryData,
  RegistryLoginType,
  RegistryUpdateCredentialsRequestData,
  RegistryUpdateRequestData,
} from "./types.js";

export class Registry extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData(
    "container",
    "registry",
  );

  public constructor(id: string) {
    super(id);
  }

  public static ofId(id: string) {
    return new Registry(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.registry.find(id);
    if (data !== undefined) {
      return new RegistryDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const registry = await this.find(id);
    assertObjectFound(registry, "registry", id);
    return registry;
  });

  public findDetailed = provideReact(
    () => Registry.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<RegistryDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Registry.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<RegistryDetailed>>;

  public static query(project: Project, query: RegistryListQueryData = {}) {
    return new RegistryListQuery(project, query);
  }

  public static async create(
    projectId: string,
    data: RegistryCreateRequestData,
  ) {
    const response = await config.behaviors.registry.create(projectId, data);

    if ("id" in response) {
      return new Registry(response.id);
    }

    return response;
  }

  public async update(data: RegistryUpdateRequestData) {
    return await config.behaviors.registry.update(this.id, {
      uri: data.uri,
      description: data.description,
    });
  }

  public async updateCredentials(
    data: RegistryUpdateCredentialsRequestData,
    loginType: RegistryLoginType,
  ) {
    const credentials =
      loginType === "password"
        ? {
            username: data.username,
            password: data.password,
          }
        : undefined;

    return await config.behaviors.registry.update(this.id, {
      credentials: credentials,
    });
  }

  public async delete() {
    await config.behaviors.registry.delete(this.id);
  }
}

class RegistryCommon extends classes(
  DataModel<RegistryListItemData | RegistryData>,
  Registry,
) {
  public readonly description: string;
  public readonly uri: string;
  public readonly username?: string;
  public readonly loginType: RegistryLoginType;
  public readonly project: Project;

  public constructor(data: RegistryListItemData | RegistryData) {
    super([data], [data.id]);
    this.description = data.description;
    this.uri = data.uri;
    this.username = data.credentials?.username;
    this.loginType = data.credentials ? "password" : "anonymous";
    this.project = Project.ofId(data.projectId);
  }

  public async validateCredentials(): Promise<boolean | undefined> {
    if (this.loginType === "anonymous") {
      return;
    }

    return await config.behaviors.registry.validateCredentials(this.id);
  }
}

export class RegistryDetailed extends classes(
  RegistryCommon,
  DataModel<RegistryData>,
) {
  public constructor(data: RegistryData) {
    super([data], [data]);
  }
}

export class RegistryListItem extends classes(
  RegistryCommon,
  DataModel<RegistryListItemData>,
) {
  public constructor(data: RegistryListItemData) {
    super([data], [data]);
  }
}

export class RegistryListQuery extends ListQueryModel<RegistryListQueryData> {
  public readonly project: Project;

  public constructor(project: Project, query: RegistryListQueryData = {}) {
    super(query, {
      dependencies: [project.id],
    });
    this.project = project;
  }

  public refine(query: RegistryListQueryData) {
    return new RegistryListQuery(this.project, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.registry.list(
      this.project.id,
      this.query,
    );
    return new RegistryList(
      this.project,
      this.query,
      items.map((r) => new RegistryListItem(r)),
    );
  }, [this.queryId, "registries"]);
}

export class RegistryList extends classes(
  RegistryListQuery,
  ListDataModel<RegistryListItem>,
) {
  public constructor(
    project: Project,
    query: RegistryListQueryData,
    registries: RegistryListItem[],
  ) {
    super([project, query], [registries, registries.length]);
  }
}
